import { z } from 'zod'
import { memberEligibilityStatusSchema } from './eligibility'
import { emailSchema, publicIdSchema, utcTimestampSchema } from './primitives'

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

export const TEAM_STATUS_VALUES = ['active', 'disbanded'] as const
export const teamStatusSchema = z.enum(TEAM_STATUS_VALUES)
export type TeamStatus = z.infer<typeof teamStatusSchema>

export const TEAM_MEMBER_ROLE_VALUES = ['leader', 'member', 'teacher'] as const
export const teamMemberRoleSchema = z.enum(TEAM_MEMBER_ROLE_VALUES)
export type TeamMemberRole = z.infer<typeof teamMemberRoleSchema>

export const TEAM_INVITE_TYPE_VALUES = ['code', 'url', 'email'] as const
export const teamInviteTypeSchema = z.enum(TEAM_INVITE_TYPE_VALUES)
export type TeamInviteType = z.infer<typeof teamInviteTypeSchema>

export const TEAM_INVITE_STATUS_VALUES = ['active', 'revoked', 'expired', 'exhausted'] as const
export const teamInviteStatusSchema = z.enum(TEAM_INVITE_STATUS_VALUES)
export type TeamInviteStatus = z.infer<typeof teamInviteStatusSchema>

/**
 * Short, human-shareable code — uppercase letters and digits, excluding `0`,
 * `1`, `I` and `O` so a code read aloud or handwritten is never ambiguous.
 */
export const teamCodeSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(
    /^[A-HJ-NP-Z2-9]{6,12}$/,
    'Must be 6-12 uppercase letters or digits (excluding 0, 1, I, O).',
  )

// ---------------------------------------------------------------------------
// Team
// ---------------------------------------------------------------------------

export const teamSchema = z.object({
  id: publicIdSchema,
  competitionId: publicIdSchema,
  categoryId: publicIdSchema,
  name: z.string().trim().min(1).max(200),
  code: teamCodeSchema,
  leaderUserId: publicIdSchema,
  status: teamStatusSchema,
  createdAt: utcTimestampSchema,
  updatedAt: utcTimestampSchema,
})
export type Team = z.infer<typeof teamSchema>

export const teamDtoSchema = teamSchema.omit({ competitionId: true })
export type TeamDto = z.infer<typeof teamDtoSchema>

export const createTeamRequestSchema = z.strictObject({
  name: z.string().trim().min(1).max(200),
  categoryId: publicIdSchema,
})
export type CreateTeamRequest = z.infer<typeof createTeamRequestSchema>

/**
 * `status` is intentionally omittable-but-present rather than dropped: only a
 * staff/admin caller (checked in the route, not here) may disband a team, but
 * the shape is shared with the leader-only name/category edit.
 */
export const updateTeamRequestSchema = z
  .strictObject({
    name: z.string().trim().min(1).max(200).optional(),
    categoryId: publicIdSchema.optional(),
    status: teamStatusSchema.optional(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: 'At least one field must be provided.',
  })
export type UpdateTeamRequest = z.infer<typeof updateTeamRequestSchema>

// ---------------------------------------------------------------------------
// Team member
// ---------------------------------------------------------------------------

export const teamMemberSchema = z.object({
  id: publicIdSchema,
  competitionId: publicIdSchema,
  teamId: publicIdSchema,
  userId: publicIdSchema,
  memberRole: teamMemberRoleSchema,
  joinedAt: utcTimestampSchema,
})
export type TeamMember = z.infer<typeof teamMemberSchema>

/** A member row plus the identity fields a teammate is allowed to see. */
export const teamMemberDtoSchema = z.object({
  id: publicIdSchema,
  userId: publicIdSchema,
  email: emailSchema,
  displayName: z.string().min(1),
  memberRole: teamMemberRoleSchema,
  joinedAt: utcTimestampSchema,
})
export type TeamMemberDto = z.infer<typeof teamMemberDtoSchema>

export const teamDetailDtoSchema = teamDtoSchema.extend({
  members: z.array(teamMemberDtoSchema),
})
export type TeamDetailDto = z.infer<typeof teamDetailDtoSchema>

// ---------------------------------------------------------------------------
// Team Operations Matrix (NAPAICP Competition Admin SDD v1.2 §9/§35, Wave B)
// ---------------------------------------------------------------------------

/**
 * One team's submission status, folded from the full `SUBMISSION_STATUS_VALUES`
 * lifecycle down to the three buckets the matrix shows: `submitted`/`locked`
 * -> `submitted`; `draft`/`ready` -> `draft`; no row, or only a
 * `withdrawn`/`disqualified` one, -> `missing`. Mirrors exactly how
 * `admin-operations.ts`'s dashboard `submissions.missing` count already
 * folds these statuses — no second classification rule.
 */
export const TEAM_SUBMISSION_STATE_VALUES = ['missing', 'draft', 'submitted'] as const
export const teamSubmissionStateSchema = z.enum(TEAM_SUBMISSION_STATE_VALUES)
export type TeamSubmissionState = z.infer<typeof teamSubmissionStateSchema>

/**
 * SDD §9.5 "Team Attention" — every flag here is derived server-side from
 * data the API already computes elsewhere (never a fabricated risk score).
 * Deliberately excludes `submissionReturned`/`conflictBlocked`/
 * `scoreIncomplete` from the v1.2 wireframe list: none of those has an
 * existing authoritative source this endpoint can read without inventing a
 * new classification rule — tracked as a SPEC-GAP for a later wave rather
 * than guessed here.
 */
export const TEAM_ATTENTION_FLAG_VALUES = [
  'registrationIncomplete',
  'eligibilityMissing',
  'eligibilityRejected',
  'eligibilityExpired',
  'submissionMissing',
  'noJudgeAssignment',
] as const
export const teamAttentionFlagSchema = z.enum(TEAM_ATTENTION_FLAG_VALUES)
export type TeamAttentionFlag = z.infer<typeof teamAttentionFlagSchema>

export const teamOperationsRowDtoSchema = teamDtoSchema.extend({
  categoryName: z.string(),
  leaderName: z.string(),
  /** Non-teacher members only, same convention as the Operations Dashboard's `participants.total`. */
  memberCount: z.number().int().min(0),
  /** BR-156: purely the registration_form_answers existence check — no eligibility dependency, ever. */
  registrationCompleted: z.boolean(),
  eligibility: z.object({
    required: z.number().int().min(0),
    submitted: z.number().int().min(0),
    approved: z.number().int().min(0),
  }),
  submissionState: teamSubmissionStateSchema,
  judging: z.object({
    assigned: z.number().int().min(0),
    completed: z.number().int().min(0),
  }),
  attention: z.array(teamAttentionFlagSchema),
})
export type TeamOperationsRowDto = z.infer<typeof teamOperationsRowDtoSchema>

/** SDD v1.2 §36 Wave G (P2) "reminder bulk actions" — `POST .../teams/registration-reminders` response. */
export const registrationReminderResultDtoSchema = z.strictObject({
  recipientCount: z.number().int().min(0),
  teamIds: z.array(publicIdSchema),
  failedTeamIds: z.array(publicIdSchema),
})
export type RegistrationReminderResultDto = z.infer<typeof registrationReminderResultDtoSchema>

export const transferTeamLeaderRequestSchema = z.strictObject({
  newLeaderUserId: publicIdSchema,
  reason: z.string().trim().min(1).max(2000),
})
export type TransferTeamLeaderRequest = z.infer<typeof transferTeamLeaderRequestSchema>

// ---------------------------------------------------------------------------
// Team invite
// ---------------------------------------------------------------------------

export const teamInviteSchema = z.object({
  id: publicIdSchema,
  competitionId: publicIdSchema,
  teamId: publicIdSchema,
  inviteType: teamInviteTypeSchema,
  invitedEmail: emailSchema.nullable(),
  createdByUserId: publicIdSchema,
  expiresAt: utcTimestampSchema,
  maxUses: z.int().min(1).nullable(),
  useCount: z.int().min(0),
  status: teamInviteStatusSchema,
  createdAt: utcTimestampSchema,
  updatedAt: utcTimestampSchema,
})
export type TeamInvite = z.infer<typeof teamInviteSchema>

/** Never carries the token — the raw token is returned once, at creation, and separately. */
export const teamInviteDtoSchema = teamInviteSchema.omit({ competitionId: true, teamId: true })
export type TeamInviteDto = z.infer<typeof teamInviteDtoSchema>

export const createTeamInviteRequestSchema = z
  .strictObject({
    inviteType: teamInviteTypeSchema,
    invitedEmail: emailSchema.nullable().default(null),
    maxUses: z.int().min(1).nullable().default(null),
    /** Defaults to 7 days server-side when omitted. */
    expiresInHours: z
      .int()
      .min(1)
      .max(24 * 90)
      .default(24 * 7),
  })
  .refine((value) => value.inviteType !== 'email' || value.invitedEmail !== null, {
    message: 'invitedEmail is required for an email invite.',
    path: ['invitedEmail'],
  })
export type CreateTeamInviteRequest = z.infer<typeof createTeamInviteRequestSchema>

/** Returned only from the create-invite endpoint. `token` is shown exactly once. */
export interface TeamInviteCreatedDto {
  invite: TeamInviteDto
  token: string
}

/**
 * PR-190/UX-165: why an invalid invite is invalid, so the frontend can show
 * a specific, honest reason ("這個邀請連結已過期" vs "這個邀請已失效")
 * instead of one generic message — still never reveals *which* team/
 * competition an invalid token would have pointed at. `null` when the
 * invite is valid.
 */
export const teamInvitePreviewReasonSchema = z.enum(['expired', 'revoked', 'exhausted', 'invalid'])
export type TeamInvitePreviewReason = z.infer<typeof teamInvitePreviewReasonSchema>

/**
 * The pre-accept preview. Deliberately thin: no team roster, no competition
 * detail beyond its name — the Phase 4 brief's explicit "must not leak team
 * membership before the token is verified" requirement. Once a token *is*
 * verified valid, `inviterDisplayName`/`memberCount`/`teamMaxMembers` add
 * the Team/Competition context `BR-133` requires — still no roster, no
 * other member's identity.
 */
export interface TeamInvitePreviewDto {
  valid: boolean
  teamName: string | null
  competitionName: string | null
  inviteType: TeamInviteType | null
  reason: TeamInvitePreviewReason | null
  inviterDisplayName: string | null
  memberCount: number | null
  teamMaxMembers: number | null
}

export const acceptTeamInviteRequestSchema = z.strictObject({
  token: z.string().trim().min(32).max(256),
  // Intentionally an integration hook, not a client-side security assertion.
  turnstileToken: z.string().trim().min(1).max(4096).optional(),
})
export type AcceptTeamInviteRequest = z.infer<typeof acceptTeamInviteRequestSchema>

/**
 * TASK-INV-A / API-PROPOSED-INV-002: `POST /api/team-invites/continuations`.
 * Same raw-token shape as `acceptTeamInviteRequestSchema` minus
 * `turnstileToken` -- this endpoint is read/validate-only (no mutation, no
 * Turnstile-gated action), so it never touches the Turnstile budget the
 * accept endpoint already spends.
 */
export const createInviteContinuationRequestSchema = z.strictObject({
  token: z.string().trim().min(32).max(256),
})
export type CreateInviteContinuationRequest = z.infer<typeof createInviteContinuationRequestSchema>

/** Response body of `POST /api/team-invites/continuations`. */
export interface InviteContinuationCreatedDto {
  continuationId: string
  expiresAt: string
}

/**
 * Response body of `POST /api/team-invites/continuations/resume`.
 * `token` is the resolved invite's own raw token (never the continuation id
 * itself -- see SEC-PROPOSED-INV-002), only ever returned to the
 * already-authenticated first-party client that is about to navigate back
 * to the invitation confirmation page; `null` when there is nothing to
 * resume (no continuation cookie, or it is missing/expired/consumed/no
 * longer usable).
 */
export interface InviteContinuationResumeDto {
  token: string | null
}

// ---------------------------------------------------------------------------
// Team form answers (Phase 3's form renderer, applied at team level)
// ---------------------------------------------------------------------------

export const teamFormAnswersSchema = z.object({
  id: publicIdSchema,
  competitionId: publicIdSchema,
  teamId: publicIdSchema,
  formId: publicIdSchema,
  formVersionId: publicIdSchema,
  answers: z.record(z.string(), z.unknown()),
  createdAt: utcTimestampSchema,
  updatedAt: utcTimestampSchema,
})
export type TeamFormAnswers = z.infer<typeof teamFormAnswersSchema>

export const teamFormAnswersDtoSchema = teamFormAnswersSchema.omit({
  competitionId: true,
  teamId: true,
})
export type TeamFormAnswersDto = z.infer<typeof teamFormAnswersDtoSchema>

export const updateTeamFormAnswersRequestSchema = z.strictObject({
  answers: z.record(z.string(), z.unknown()),
})
export type UpdateTeamFormAnswersRequest = z.infer<typeof updateTeamFormAnswersRequestSchema>

// ---------------------------------------------------------------------------
// Participants (Competition Admin SDD v1.1 §11) — deliberately distinct from
// "Members & Roles" (competition staff/governance roles, `competition_roles`
// table): a Participant is an actual competitor (team leader/member). This
// DTO carries exactly the SDD §11.2 columns, nothing more (no proof
// binaries, no signed URLs, no extra PII).
// ---------------------------------------------------------------------------

export const PARTICIPANT_REGISTRATION_STATUS_VALUES = ['complete', 'incomplete'] as const
export const participantRegistrationStatusSchema = z.enum(PARTICIPANT_REGISTRATION_STATUS_VALUES)
export type ParticipantRegistrationStatus = z.infer<typeof participantRegistrationStatusSchema>

export const participantDtoSchema = z.object({
  userId: publicIdSchema,
  displayName: z.string(),
  email: emailSchema,
  teamId: publicIdSchema,
  teamName: z.string(),
  teamRole: z.enum(['leader', 'member']),
  categoryId: publicIdSchema,
  categoryName: z.string(),
  schoolName: z.string().nullable(),
  departmentName: z.string().nullable(),
  eligibilityStatus: memberEligibilityStatusSchema,
  registrationStatus: participantRegistrationStatusSchema,
  updatedAt: utcTimestampSchema,
})
export type ParticipantDto = z.infer<typeof participantDtoSchema>

/** Server-side pagination/search/filter/sort contract for `GET /:competitionId/participants`. */
export const participantListQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(25),
  offset: z.coerce.number().int().min(0).default(0),
  search: z.string().trim().max(200).optional(),
  teamId: publicIdSchema.optional(),
  categoryId: publicIdSchema.optional(),
  eligibilityStatus: memberEligibilityStatusSchema.optional(),
  registrationStatus: participantRegistrationStatusSchema.optional(),
  sort: z.enum(['name', 'updatedAt']).optional(),
})
export type ParticipantListQuery = z.infer<typeof participantListQuerySchema>

/**
 * SDD §11.3 Participant Summary header's leader/member split — the ONE
 * number this page cannot source from the existing Operations Dashboard
 * aggregation contract (`GET .../admin/dashboard`), which has no
 * leader/member breakdown (see `TeamMembersRepository.countByRoleForCompetition`).
 * Every other §11.3 figure (total participants, teams, eligibility
 * breakdown) MUST come from that dashboard contract instead — never
 * re-derived client-side from a (possibly filtered/paged) participant list.
 */
export interface ParticipantRoleSummaryDto {
  leaders: number
  members: number
}
